'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import LoginForm from '@/components/forms/LoginForm';
import { Building2, ArrowLeft, Lock, Shield } from 'lucide-react';

interface CompanyOption {
  id: string;
  name: string;
  slug: string;
  role: string;
  is_active: boolean;
  plan_name: string;
}

export default function LoginPage() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectingCompany, setIsSelectingCompany] = useState(false);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const router = useRouter();

  const handleLogin = async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success && data.requireCompanySelection) {
        // Usuario pertenece a multiples empresas - mostrar selector
        setCompanies(data.companies || []);
        setTempToken(data.tempToken || null);
        setUserId(data.userId || null);
        setFirstName(data.firstName || null);
        setIsSelectingCompany(true);
      } else if (response.ok && data.success) {
        // Login exitoso con empresa unica, redirigir al dashboard
        router.push('/dashboard');
      } else {
        // Login fallido
        setError(data.message || 'Error al iniciar sesion');
      }
    } catch (error) {
      console.error('Error en login:', error);
      setError('Error de conexion. Por favor, intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectCompany = async (companyId: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (tempToken) {
        headers['Authorization'] = `Bearer ${tempToken}`;
      }

      const response = await fetch('/api/auth/select-company', {
        method: 'POST',
        headers,
        body: JSON.stringify({ companyId, userId }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        router.push('/dashboard');
      } else {
        setError(data.message || 'Error al seleccionar empresa');
      }
    } catch (error) {
      console.error('Error seleccionando empresa:', error);
      setError('Error de conexion. Por favor, intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setIsSelectingCompany(false);
    setCompanies([]);
    setTempToken(null);
    setUserId(null);
    setError(null);
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
            <Shield className="w-3 h-3 mr-1" />
            Owner
          </span>
        );
      case 'admin':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
            <Shield className="w-3 h-3 mr-1" />
            Admin
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
            {role.charAt(0).toUpperCase() + role.slice(1)}
          </span>
        );
    }
  };

  // Company selection screen
  if (isSelectingCompany) {
    const displayName = firstName || 'usuario';

    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        padding: '40px 20px'
      }}>
        <div style={{ maxWidth: '560px', width: '100%' }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '50px 40px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{
                width: '64px', height: '64px', background: '#2563eb', borderRadius: '16px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px'
              }}>
                <Building2 style={{ width: '32px', height: '32px', color: 'white' }} />
              </div>
              <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0 0 8px 0' }}>
                Bienvenido {displayName}
              </h1>
              <p style={{ fontSize: '15px', color: '#6b7280', margin: 0 }}>
                ¿Desde donde quieres trabajar hoy?
              </p>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: '8px', marginBottom: '20px', textAlign: 'center'
              }}>
                <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>{error}</p>
              </div>
            )}

            {/* Company cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {companies.map((company) => {
                const isSuspended = !company.is_active;

                return (
                  <button
                    key={company.id}
                    onClick={() => !isSuspended && !isLoading && handleSelectCompany(company.id)}
                    disabled={isSuspended || isLoading}
                    style={{
                      width: '100%', textAlign: 'left', borderRadius: '12px',
                      border: '1px solid ' + (isSuspended ? '#e5e7eb' : '#e5e7eb'),
                      padding: '16px 20px', background: isSuspended ? '#f9fafb' : 'white',
                      cursor: isSuspended ? 'not-allowed' : 'pointer',
                      opacity: isSuspended ? 0.6 : 1,
                      transition: 'all 0.2s',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                    }}
                    onMouseEnter={(e) => { if (!isSuspended) { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)'; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{
                        width: '48px', height: '48px', borderRadius: '12px',
                        background: isSuspended ? '#e5e7eb' : '#2563eb',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: isSuspended ? '#9ca3af' : 'white',
                        fontSize: '18px', fontWeight: '700', flexShrink: 0
                      }}>
                        {company.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p style={{ fontSize: '15px', fontWeight: '600', color: isSuspended ? '#9ca3af' : '#1f2937', margin: '0 0 4px 0' }}>
                          {company.name}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center',
                            padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600',
                            background: company.role === 'owner' ? '#fef3c7' : company.role === 'admin' ? '#dbeafe' : '#f3f4f6',
                            color: company.role === 'owner' ? '#92400e' : company.role === 'admin' ? '#1e40af' : '#4b5563'
                          }}>
                            {company.role === 'owner' ? 'Propietario' : company.role === 'admin' ? 'Admin' : company.role}
                          </span>
                          {company.plan_name && (
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>{company.plan_name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      {isSuspended ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '4px 10px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600',
                          background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca'
                        }}>
                          <Lock style={{ width: '12px', height: '12px' }} />
                          Suspendida
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '4px 10px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600',
                          background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0'
                        }}>
                          Activa
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Back button */}
            <div style={{ textAlign: 'center', marginTop: '28px' }}>
              <button
                onClick={handleBackToLogin}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '14px', color: '#2563eb', fontWeight: '500'
                }}
              >
                <ArrowLeft style={{ width: '14px', height: '14px' }} />
                Cerrar sesion
              </button>
            </div>

            <div style={{
              marginTop: '20px', padding: '14px', background: '#f9fafb',
              borderRadius: '8px', border: '1px solid #e5e7eb'
            }}>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0, textAlign: 'center', lineHeight: '1.5' }}>
                Solo se muestran empresas donde tienes rol de administrador.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal login screen
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f5f5',
      padding: '40px 20px'
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        maxWidth: '1200px',
        width: '100%',
        gap: '24px'
      }}>
        {/* Card izquierda - Informacion */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '50px 40px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          {/* Header con logo */}
          <div style={{
            background: '#2563eb',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              margin: '0 0 8px 0',
              lineHeight: '1.2',
              color: 'white'
            }}>
              Portal Empresarial AbbiAI
            </h1>
            <p style={{
              fontSize: '14px',
              margin: 0,
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              Administra usuarios, planes y consumo de tu organizacion
            </p>
          </div>

          {/* Funcionalidades */}
          <div>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '16px',
              marginTop: 0,
              color: '#1f2937'
            }}>
              Funcionalidades Principales
            </h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              {[
                'Gestion de usuarios y roles de tu empresa',
                'Asignacion de sub-planes y modelos de IA',
                'Monitoreo de consumo de tokens en tiempo real',
                'Dashboard con metricas y alertas',
                'Reportes de uso por usuario',
                'Configuracion de la organizacion'
              ].map((item, index) => (
                <li key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: '#4b5563'
                }}>
                  <span style={{
                    width: '6px',
                    height: '6px',
                    background: '#2563eb',
                    borderRadius: '50%',
                    flexShrink: 0
                  }}></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Acceso Seguro */}
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              marginTop: 0,
              marginBottom: '6px',
              color: '#1f2937'
            }}>
              Acceso Seguro
            </h4>
            <p style={{
              fontSize: '13px',
              margin: 0,
              lineHeight: '1.5',
              color: '#6b7280'
            }}>
              Acceso protegido con autenticacion segura. Tus credenciales fueron creadas desde el portal corporativo.
            </p>
          </div>
        </div>

        {/* Card derecha - Formulario */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '50px 40px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <LoginForm
            onSubmit={handleLogin}
            isLoading={isLoading}
            error={error}
          />
        </div>
      </div>
    </div>
  );
}
